import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    Divider,
    MenuItem,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
    Autocomplete,
} from '@mui/material';
import {
    Close as CloseIcon,
    FolderOpen as FolderOpenIcon,
    Info as InfoIcon,
    ChevronRight as ChevronRightIcon,
    FileCopy as FileIcon,
    Home as HomeIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { getRepositoryExplore, createBatch, getRepositoryList, getRepositoryBuckets } from '../../apis/repository';
import { useDebounce } from '../../hooks/useDebounce';

// API function to fetch repositories
const fetchRepositories = async () => {
    try {
        // Get first 100 repositories & sort by name in ascending order
        const response = await getRepositoryList(1, 100, null, null, null, { field: 'name', sort: 'asc' });
        return response.results || [];
    } catch (error) {}
};

// API function to fetch buckets for a specific repository
const fetchBuckets = async (repositoryId) => {
    try {
        const response = await getRepositoryBuckets(repositoryId);
        const buckets = extractBuckets(response);
        if (Array.isArray(buckets)) return buckets;
        return [];
    } catch (error) {
        return [];
    }
};

// Helper to extract bucket array from different response shapes
const extractBuckets = (response) => {
    if (Array.isArray(response)) return response;
    if (response?.results && Array.isArray(response.results)) return response.results;
    if (response?.buckets && Array.isArray(response.buckets)) return response.buckets;
    return null;
};

// Yup validation schema
const validationSchema = Yup.object({
    batch_name: Yup.string()
        .required('Batch name is required')
        .min(2, 'Batch name must be at least 2 characters')
        .max(50, 'Batch name must be less than 50 characters'),
    repository: Yup.string().required('Repository is required'),
    bucket: Yup.string()
        .required('Bucket is required')
        .min(3, 'Bucket name must be at least 3 characters')
        .max(300, 'Bucket name must be less than 300 characters'),
    path: Yup.string().required('Please select a folder to process'),
});

const CreateBatchDialog = ({ open, onClose, onSuccess, repository }) => {
    const [folderData, setFolderData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentPath, setCurrentPath] = useState('/');
    const [selectedFolders, setSelectedFolders] = useState([]);
    const [_expandedFolders, setExpandedFolders] = useState(new Set());
    const [pathHistory, setPathHistory] = useState(['/']); // Track navigation history
    const [repositories, setRepositories] = useState([]);
    const [buckets, setBuckets] = useState([]);
    const [loadingRepositories, setLoadingRepositories] = useState(false);
    const [loadingBuckets, setLoadingBuckets] = useState(false);
    const [bucketName, setBucketName] = useState(''); // Track bucket name for debouncing

    // Debounce bucket name to avoid excessive API calls while typing
    const debouncedBucketName = useDebounce(bucketName, 800);

    const formik = useFormik({
        initialValues: {
            batch_name: '',
            repository: repository?.id || '',
            bucket: '',
            path: '',
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                const batchData = {
                    batch_name: values.batch_name,
                    repository: values.repository,
                    bucket: values.bucket,
                    path: values.path,
                };

                await createBatch(batchData);
                onSuccess();
                handleClose();
            } catch (error) {
                let errorMessage = 'Failed to create batch';

                if (error.response && error.response.data && error.response.data.error) {
                    const apiError = error.response.data.error;
                    errorMessage = Array.isArray(apiError) ? apiError.join(', ') : apiError;
                } else if (error.message) errorMessage = error.message;

                toast.error(errorMessage);
            } finally {
                setSubmitting(false);
            }
        },
    });

    // API call to fetch folder data
    const fetchFolderData = async (path = '/', bucket = '') => {
        if (!formik.values.repository) return;

        // Use provided bucket or fall back to debounced bucket name
        const bucketToUse = bucket || debouncedBucketName;

        // Don't fetch if bucket is required but not provided
        if (!bucketToUse) {
            setFolderData({ path, children: [] });
            return;
        }

        setLoading(true);
        try {
            const response = await getRepositoryExplore(formik.values.repository, path, bucketToUse);
            setFolderData(response);
        } catch (error) {
            toast.error('Failed to load folder structure');
            // Fallback to empty data on error
            setFolderData({ path, children: [] });
        } finally {
            setLoading(false);
        }
    };

    // API call to fetch repositories
    const fetchRepositoriesList = async () => {
        setLoadingRepositories(true);
        try {
            const response = await fetchRepositories();
            setRepositories(response);
        } catch (error) {
            toast.error('Failed to load repositories');
            setRepositories([]);
        } finally {
            setLoadingRepositories(false);
        }
    };

    // API call to fetch buckets for selected repository
    const fetchBucketsList = async (repositoryId) => {
        if (!repositoryId) {
            setBuckets([]);
            return;
        }

        setLoadingBuckets(true);
        try {
            const response = await fetchBuckets(repositoryId);
            // Ensure we always set an array
            setBuckets(Array.isArray(response) ? response : []);
        } catch (error) {
            toast.error('Failed to load buckets');
            setBuckets([]);
        } finally {
            setLoadingBuckets(false);
        }
    };

    // Load repositories when component mounts
    useEffect(() => {
        fetchRepositoriesList();
    }, []);

    // Update formik values when repository prop changes and fetch initial folder data
    useEffect(() => {
        if (repository?.id) {
            formik.setFieldValue('repository', repository.id);
            fetchFolderData();
            fetchBucketsList(repository.id);
            // Initialize with root folder selected
            setSelectedFolders([{ path: '/', name: 'Root' }]);
            formik.setFieldValue('path', '/');
        }
    }, [repository]);

    // Fetch buckets when repository selection changes
    useEffect(() => {
        if (formik.values.repository) {
            fetchBucketsList(formik.values.repository);
            // Reset bucket selection when repository changes
            formik.setFieldValue('bucket', '');
            setBucketName('');
        } else {
            setBuckets([]);
            formik.setFieldValue('bucket', '');
            setBucketName('');
        }
    }, [formik.values.repository]);

    // Fetch folders when debounced bucket name changes
    useEffect(() => {
        if (debouncedBucketName && formik.values.repository) {
            // Reset to root path when bucket changes
            setCurrentPath('/');
            setPathHistory(['/']);
            setSelectedFolders([{ path: '/', name: 'Root' }]);
            formik.setFieldValue('path', '/');

            // Fetch folder data for the new bucket
            fetchFolderData('/', debouncedBucketName);
        } else if (!debouncedBucketName) {
            // Clear folder data if no bucket selected
            setFolderData(null);
        }
    }, [debouncedBucketName, formik.values.repository]);

    // Reset to root when modal opens
    useEffect(() => {
        if (open) {
            // Reset all states to initial values
            setCurrentPath('/');
            setSelectedFolders([{ path: '/', name: 'Root' }]);
            setExpandedFolders(new Set());
            setPathHistory(['/']);
            setFolderData(null);
            setBucketName('');

            // Reset formik values
            formik.setFieldValue('batch_name', '');
            formik.setFieldValue('repository', repository?.id || '');
            formik.setFieldValue('bucket', '');
            formik.setFieldValue('path', '/');

            // Fetch data if repository is provided
            if (repository?.id) {
                fetchBucketsList(repository.id);
            }
        }
    }, [open]);

    const handleFolderClick = (folder) => {
        if (folder.type === 'directory' && formik.values.repository && debouncedBucketName) {
            // Build the full path by appending to current path
            const newPath = folder.path;
            setCurrentPath(newPath);

            // Add to path history for navigation
            setPathHistory((prev) => [...prev, newPath]);

            fetchFolderData(newPath, debouncedBucketName);

            // Update selected path to the folder we're navigating into
            setSelectedFolders([{ path: newPath, name: folder.name }]);
            formik.setFieldValue('path', newPath);
        }
    };

    // const handleToggleExpand = (folder, event) => {
    //     event.stopPropagation();

    //     if (!formik.values.repository || !debouncedBucketName) return;

    //     // Expand/collapse the folder
    //     const newExpanded = new Set(expandedFolders);
    //     if (newExpanded.has(folder.path)) {
    //         newExpanded.delete(folder.path);
    //     } else {
    //         newExpanded.add(folder.path);
    //     }
    //     setExpandedFolders(newExpanded);

    //     // Also select this folder and navigate into it (like Windows Explorer)
    //     const newPath = folder.path;
    //     setCurrentPath(newPath);

    //     // Add to path history for navigation
    //     setPathHistory((prev) => [...prev, newPath]);

    //     fetchFolderData(newPath, debouncedBucketName);
    //     setSelectedFolders([{ path: newPath, name: folder.name }]);
    //     formik.setFieldValue('path', newPath);
    // };

    // API call to fetch specific folder details for selected folder
    // const fetchFolderDetails = async (path) => {
    //     if (!formik.values.repository || !debouncedBucketName) return;

    //     try {
    //         const response = await getRepositoryExplore(formik.values.repository, path, debouncedBucketName);
    //         // Update the selected folder with detailed information
    //         setSelectedFolders([
    //             {
    //                 ...(selectedFolders[0] || { path, name: path.split('/').pop() }),
    //                 children: response.children,
    //                 path: response.path,
    //             },
    //         ]);
    //     } catch (error) {
    //         console.error('Error fetching folder details:', error);
    //     }
    // };

    const handleNavigateBack = (targetPath) => {
        if (!debouncedBucketName) return;

        setCurrentPath(targetPath);
        fetchFolderData(targetPath, debouncedBucketName);

        // Update selected folders to the target path
        const pathParts = targetPath.split('/').filter((part) => part !== '');
        const folderName = pathParts.length > 0 ? pathParts[pathParts.length - 1] : 'Root';
        setSelectedFolders([{ path: targetPath, name: folderName }]);
        formik.setFieldValue('path', targetPath);

        // Update path history to remove paths after the target
        setPathHistory((prev) => {
            const targetIndex = prev.indexOf(targetPath);
            return targetIndex >= 0 ? prev.slice(0, targetIndex + 1) : prev;
        });
    };

    const handleClose = () => {
        formik.resetForm();

        setSelectedFolders([]);
        setCurrentPath('/');
        setFolderData(null);
        setExpandedFolders(new Set());
        setPathHistory(['/']);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    minHeight: '600px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontWeight: 600,
                    py: 2,
                    px: 0,
                }}
            >
                Create New Batch
                <IconButton
                    onClick={handleClose}
                    disabled={formik.isSubmitting}
                    sx={{
                        p: 1,
                    }}
                >
                    <CloseIcon sx={{ fontSize: '1.25rem', color: '#666' }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ padding: '2rem 0' }}>
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{ display: 'flex', gap: 3, height: '500px' }}>
                        {/* LEFT SIDE - Form Fields */}
                        <Box sx={{ flex: '0 0 350px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Batch Name */}
                            <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                                    Batch Name *
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    name="batch_name"
                                    value={formik.values.batch_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.batch_name && Boolean(formik.errors.batch_name)}
                                    helperText={formik.touched.batch_name && formik.errors.batch_name}
                                    disabled={formik.isSubmitting}
                                    placeholder="Enter batch name"
                                />
                            </Box>

                            {/* Repository */}
                            <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                                    Select Repository *
                                </Typography>
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    name="repository"
                                    value={formik.values.repository}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.repository && Boolean(formik.errors.repository)}
                                    helperText={formik.touched.repository && formik.errors.repository}
                                    disabled={formik.isSubmitting || loadingRepositories}
                                    placeholder="Select a repository"
                                >
                                    {loadingRepositories ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={16} sx={{ mr: 1 }} />
                                            Loading repositories...
                                        </MenuItem>
                                    ) : repositories.length === 0 ? (
                                        <MenuItem disabled>No repositories available</MenuItem>
                                    ) : (
                                        repositories.map((repo) => (
                                            <MenuItem key={repo.id} value={repo.id}>
                                                {repo.name} ({repo.type})
                                            </MenuItem>
                                        ))
                                    )}
                                </TextField>
                            </Box>

                            {/* Bucket */}
                            <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                                    Select or Enter Bucket *
                                </Typography>
                                <Autocomplete
                                    fullWidth
                                    freeSolo
                                    size="small"
                                    options={Array.isArray(buckets) ? buckets : []}
                                    getOptionLabel={(option) => {
                                        // Handle string input (when user types)
                                        if (typeof option === 'string') return option;
                                        // Handle object from list
                                        return option.name || option.id || '';
                                    }}
                                    value={formik.values.bucket}
                                    onChange={(event, newValue) => {
                                        // Handle object or string value
                                        const bucketValue =
                                            typeof newValue === 'string' ? newValue : newValue?.name || newValue?.id || '';
                                        formik.setFieldValue('bucket', bucketValue);
                                        setBucketName(bucketValue);
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        // Update formik value and bucket name when user types
                                        if (event?.type === 'change') {
                                            formik.setFieldValue('bucket', newInputValue);
                                            setBucketName(newInputValue);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                    disabled={formik.isSubmitting || loadingBuckets || !formik.values.repository}
                                    loading={loadingBuckets}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            name="bucket"
                                            placeholder={
                                                !formik.values.repository
                                                    ? 'Select a repository first'
                                                    : loadingBuckets
                                                    ? 'Loading buckets...'
                                                    : 'Type bucket name or select from list'
                                            }
                                            error={formik.touched.bucket && Boolean(formik.errors.bucket)}
                                            helperText={
                                                formik.touched.bucket && formik.errors.bucket
                                                    ? formik.errors.bucket
                                                    : "You can type the bucket name if it's not in the list"
                                            }
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingBuckets ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    noOptionsText={
                                        !formik.values.repository
                                            ? 'Select a repository first'
                                            : 'No buckets found - you can type the bucket name'
                                    }
                                />
                            </Box>

                            {/* Selected Folder Path */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                                        Selected Folder Path *
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="path"
                                        value={
                                            selectedFolders.length > 0
                                                ? selectedFolders[0].path
                                                : currentPath === '/'
                                                ? 'Root (/)'
                                                : currentPath
                                        }
                                        disabled
                                        placeholder="Select a folder from the explorer"
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: '#666',
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem',
                                            },
                                        }}
                                    />
                                    {formik.touched.path && formik.errors.path && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {formik.errors.path}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Processing Info Alert */}
                                <Alert severity="info" icon={<InfoIcon />} sx={{ alignItems: 'center' }}>
                                    <Typography variant="caption">
                                        Subfolders and their contents will be processed automatically
                                    </Typography>
                                </Alert>
                            </Box>
                        </Box>

                        {/* RIGHT SIDE - Folder Explorer */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                                Folder Explorer *
                            </Typography>

                            {/* Breadcrumb Navigation */}
                            <Box
                                sx={{
                                    mb: 1,
                                    p: 1,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {pathHistory.map((path, index) => {
                                    const isLast = index === pathHistory.length - 1;
                                    const pathParts = path.split('/').filter((part) => part !== '');
                                    const displayName = path === '/' ? 'Root' : pathParts[pathParts.length - 1];

                                    return (
                                        <React.Fragment key={path}>
                                            {index === 0 ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleNavigateBack('/')}
                                                    sx={{
                                                        p: 0.5,
                                                        color: isLast ? '#1976d2' : '#666',
                                                        '&:hover': { backgroundColor: '#e3f2fd' },
                                                    }}
                                                >
                                                    <HomeIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>
                                            ) : (
                                                <Box
                                                    onClick={() => !isLast && handleNavigateBack(path)}
                                                    sx={{
                                                        cursor: isLast ? 'default' : 'pointer',
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 0.5,
                                                        backgroundColor: isLast ? '#e3f2fd' : 'transparent',
                                                        color: isLast ? '#1976d2' : '#666',
                                                        fontSize: '0.875rem',
                                                        fontWeight: isLast ? 600 : 400,
                                                        '&:hover': !isLast ? { backgroundColor: '#e3f2fd' } : {},
                                                    }}
                                                >
                                                    {displayName}
                                                </Box>
                                            )}
                                            {!isLast && <ChevronRightIcon sx={{ fontSize: '1rem', color: '#999' }} />}
                                        </React.Fragment>
                                    );
                                })}
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        flex: 1,
                                        backgroundColor: '#fafafa',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            backgroundColor: '#f5f5f5',
                                            borderBottom: '1px solid #e0e0e0',
                                            fontSize: '0.875rem',
                                            color: '#666',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <InfoIcon sx={{ fontSize: '1rem', color: '#1976d2' }} />
                                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                            Click a folder to select it. The number of courses inside is displayed.
                                        </Typography>
                                    </Box>

                                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                                        <List dense sx={{ py: 0 }}>
                                            {/* Show folders and files as siblings */}
                                            {(() => {
                                                const directories =
                                                    folderData?.children?.filter((child) => child.type === 'directory') ||
                                                    [];
                                                const files =
                                                    folderData?.children?.filter((child) => child.type === 'file') || [];

                                                // Create a combined list with directories first, then files count
                                                const items = [
                                                    ...directories.map((folder) => ({ ...folder, isFolder: true })),
                                                    ...(files.length > 0
                                                        ? [
                                                              {
                                                                  name: `${files.length} packages`,
                                                                  type: 'file',
                                                                  isFolder: false,
                                                                  fileCount: files.length,
                                                              },
                                                          ]
                                                        : []),
                                                ];

                                                return items.map((item, index) => {
                                                    if (item.isFolder) {
                                                        const isSelected = selectedFolders.some((f) => f.path === item.path);
                                                        return (
                                                            <ListItem
                                                                key={`folder-${index}`}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor: isSelected ? '#e3f2fd' : '#f0f0f0',
                                                                    },
                                                                    borderBottom: '1px solid #f0f0f0',
                                                                    py: 0.5,
                                                                    pl: 2,
                                                                }}
                                                                onClick={() => handleFolderClick(item)}
                                                            >
                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                    <FolderOpenIcon sx={{ color: '#666' }} />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={item.name}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.875rem',
                                                                        fontWeight: isSelected ? 600 : 400,
                                                                        color: isSelected ? '#1976d2' : 'inherit',
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        );
                                                    } else {
                                                        return (
                                                            <ListItem
                                                                key={`files-${index}`}
                                                                sx={{
                                                                    py: 0.5,
                                                                    pl: 2,
                                                                    borderBottom: '1px solid #f0f0f0',
                                                                }}
                                                            >
                                                                <ListItemIcon sx={{ minWidth: 32 }} color="text.primary">
                                                                    <FileIcon sx={{ fontSize: '1.3rem' }} />
                                                                </ListItemIcon>
                                                                <ListItemText primary={item.name} color="text.primary" />
                                                            </ListItem>
                                                        );
                                                    }
                                                });
                                            })()}
                                        </List>

                                        {/* Show no directories found if no folders */}
                                        {formik.values.repository &&
                                            debouncedBucketName &&
                                            !folderData?.children?.filter((child) => child.type === 'directory').length &&
                                            !folderData?.children?.filter((child) => child.type === 'file').length && (
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flex: 1,
                                                    }}
                                                >
                                                    <Alert severity="warning" icon={<InfoIcon />}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            No Files or Directories Found
                                                        </Typography>
                                                    </Alert>
                                                </Box>
                                            )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </form>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
                {/* Warning message when folder is empty */}

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
                    <Button variant="outlined" color="secondary" onClick={handleClose} disabled={formik.isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={formik.handleSubmit}
                        disabled={
                            formik.isSubmitting || !folderData || !folderData.children || folderData.children.length === 0
                        }
                        type="submit"
                    >
                        {formik.isSubmitting ? 'Creating...' : 'Create Batch'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CreateBatchDialog;
